/**
 * Person dossier — the diary theme's cheerful "case file" for a person.
 *
 * Purely visual: the people surfaces have two data planes (engine-captured
 * people and hand-made graph nodes) with different shapes and CRUD, so this
 * component takes already-flattened display props and renders the folder.
 * Callers branch on isDiary themselves — they must render their modern card
 * in the other branch, so an internal gate would only hide bugs.
 */
import React from 'react'

/** Small stable hash so a person keeps their folder color and portrait ink. */
function hashId(id: number | string): number {
  const s = String(id)
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

const PORTRAIT_INKS = ['#7851A9', '#4A90A4', '#C45C4A', '#4A7C59', '#B8860B', '#9B597B']

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2)
  return parts[0][0] + parts[parts.length - 1][0]
}

export interface DossierField {
  label: string
  value: string
}

export interface PersonDossierProps {
  /** Stable id — keeps the pastel tint and portrait color from jumping. */
  id: number | string
  name: string
  /** Relationship in their own words ("my daughter") — the rubber stamp. */
  relation?: string | null
  aliases?: string[]
  /** One-or-two sentence rollup, shown as the handwritten lead. */
  summary?: string | null
  /** Recent evidence lines, shown as quiet serif notes under the lead. */
  notes?: string[]
  /** Typewritten data rows (AGE, OCCUPATION, INTERESTS, ...). */
  fields?: DossierField[]
  tags?: string[]
  /** Count of linked Life-Map items, shown on the folder tab. */
  itemCount?: number
  /** Top-right corner content: action buttons, chevrons. */
  corner?: React.ReactNode
  footer?: React.ReactNode
  className?: string
}

export const PersonDossier: React.FC<PersonDossierProps> = ({
  id,
  name,
  relation,
  aliases = [],
  summary,
  notes = [],
  fields = [],
  tags = [],
  itemCount,
  corner,
  footer,
  className = '',
}) => {
  const h = hashId(id)
  const tint = `yn-tint-${h % 6}`
  const ink = PORTRAIT_INKS[h % PORTRAIT_INKS.length]

  return (
    <div className={`yn-dossier ${tint} mt-3 ${className}`}>
      <div className="yn-dossier-tab">
        dossier
        {typeof itemCount === 'number' && itemCount > 0 ? ` · ${itemCount} on map` : ''}
      </div>
      <div className="yn-dossier-body p-4">
        <div className="flex items-start gap-4">
          <div className="yn-dossier-photo shrink-0">
            <div
              className="yn-dossier-initials w-14 h-14 text-2xl"
              style={{ background: ink }}
              aria-hidden
            >
              {initialsOf(name)}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2">
              <h3 className="yn-dossier-value text-xl font-bold truncate flex-1">{name}</h3>
              {corner}
            </div>
            {relation && <div className="yn-dossier-stamp mt-1">{relation}</div>}
            {aliases.length > 0 && (
              <div className="mt-1.5">
                <span className="yn-dossier-label">a.k.a. </span>
                <span className="yn-dossier-value text-sm">“{aliases.join('”, “')}”</span>
              </div>
            )}
          </div>
        </div>

        {summary && <p className="yn-dossier-value mt-3">{summary}</p>}

        {notes.length > 0 && (
          <div className="mt-2 space-y-1">
            {notes.map((n, i) => (
              <p key={i} className="yn-dossier-note text-sm">{n}</p>
            ))}
          </div>
        )}

        {fields.length > 0 && (
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
            {fields.map((f) => (
              <div key={f.label} className="min-w-0">
                <dt className="yn-dossier-label">{f.label}</dt>
                <dd className="yn-dossier-value text-sm break-words">{f.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className="yn-postcard-chip">{t}</span>
            ))}
          </div>
        )}

        {footer && <div className="mt-3">{footer}</div>}
      </div>
    </div>
  )
}

export default PersonDossier
